import React, { ReactElement, useContext } from 'react';
import { Filter } from '@fabric-ui/components';
import { flex, shadowInsetBorder } from '@fabric-ui/theme';
import { emptyObject, noop } from '@fabric-ui/utils';
import { LinearProgress, SxProps } from '@mui/material';
import { ChartClickInteraction } from '../../../components/Widgets/types';
import { AsyncQueryStatus } from '../../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults.utils';
import useElementOnScreen from '../../../hooks/useElementOnScreen';
import { defaultWidget } from '../constants';
import { CustomDashboardContext } from '../CustomDashboardContext';
import { useGetWidgetData } from '../hooks/useGetWidgetData';
import { TableTypeWidget, WidgetCategory } from '../types';
import { AllTypeWidgets } from '../types/combined.types';
import { getParamFromURL } from './Clickthrough/utils';
import Widget from './Widget/Widget';
import FloatingWidgetLoadingState from './Widget/WidgetHistoricLoadingState/FloatingWidgetLoadingState';
import TableWidget from './Widgets/TableWidget';
import WidgetToolbar from './WidgetToolbar';

interface WidgetProps {
  widget: AllTypeWidgets;
  setWidget?: (newWidget: any) => void;
  globalFilters?: Filter;
  children: any;
  showToolbar?: boolean;
  grabbable?: boolean;
  customToolbar?: ReactElement<any>;
  isTable?: boolean;
  setIsTable?: any;
  selected?: any;
  onSelect?: (v) => void;
  clickInteractions?: ChartClickInteraction[];
  sx?: SxProps;
  enableResponsiveLegend?: boolean;
}

const DraggableWidget: React.FC<WidgetProps> = ({
  widget,
  setWidget = noop,
  globalFilters,
  children,
  showToolbar = true,
  grabbable = false,
  isTable,
  setIsTable,
  customToolbar = <WidgetToolbar widget={widget} setWidget={setWidget} isTable={isTable} setIsTable={setIsTable} />,
  selected,
  onSelect,
  clickInteractions,
  sx = emptyObject,
  enableResponsiveLegend,
}) => {
  const { filters } = useContext(CustomDashboardContext);
  const isEditMode = getParamFromURL<boolean>(filters.isEditMode);
  const { ref, isVisible } = useElementOnScreen<HTMLDivElement>({});
  const { isRefetching, responseCalculatedAt, isAsyncQuery, asyncQueryStatus } = useGetWidgetData({
    widget,
    contextFilter: globalFilters,
    visible: isVisible,
    debounceQueries: false,
  });
  const tableViewWidget = {
    ...defaultWidget,
    requests: widget.requests,
  };

  return (
    <>
      {isRefetching && <LinearProgress sx={{ position: 'absolute', width: '100%', height: '2px' }} />}
      <FloatingWidgetLoadingState
        ref={ref}
        isLoading={asyncQueryStatus === AsyncQueryStatus.Refreshing}
        dataUpdateAt={asyncQueryStatus === AsyncQueryStatus.Refreshing && responseCalculatedAt ? new Date(responseCalculatedAt) : undefined}
        enabled={isAsyncQuery}
        sx={{
          pl: 3,
          pr: 2,
          pt: 2,
          height: '100%',
          boxShadow: shadowInsetBorder,
          ...flex.col,
          cursor: isEditMode && grabbable ? 'grab' : 'default',
          ...([WidgetCategory.Tile, WidgetCategory.Text].includes(widget.category) ? { overflow: 'hidden' } : {}),
          ...sx,
        }}>
        {showToolbar && customToolbar}
        {isTable ? (
          <Widget widget={tableViewWidget as AllTypeWidgets} isEdit={false} visible={isVisible} globalFilters={globalFilters}>
            <TableWidget widget={tableViewWidget as TableTypeWidget} />
          </Widget>
        ) : (
          <Widget
            widget={widget}
            setWidget={setWidget}
            isEdit={false}
            visible={isVisible}
            globalFilters={globalFilters}
            selected={selected}
            onSelect={onSelect}
            clickInteractions={clickInteractions}
            enableResponsiveLegend={enableResponsiveLegend}>
            {children}
          </Widget>
        )}
      </FloatingWidgetLoadingState>
    </>
  );
};

export default DraggableWidget;
